<script setup>
// Avatar identificador del bot estilo Kommo (Victor 2026-05-28). Solo CSS/SVG.
// Cara redonda blanca con antena dorada-azul, audífonos azules a los lados,
// ojos cerrados y sonrisita. Badge del canal (WhatsApp/Instagram/Facebook/TikTok)
// se sobrepone abajo-derecha según channelType.
import { computed } from 'vue';

const props = defineProps({
  size: { type: Number, default: 32 },
  channelType: { type: String, default: '' },
});

const cssVars = computed(() => ({
  '--bot-size': `${props.size}px`,
}));

const channel = computed(() => {
  const t = (props.channelType || '').toLowerCase();
  if (t.includes('whatsapp')) return 'wa';
  if (t.includes('instagram')) return 'ig';
  if (t.includes('facebook')) return 'fb';
  if (t.includes('tiktok')) return 'tt';
  return null;
});
</script>

<template>
  <div class="bot-avatar" :style="cssVars">
    <div class="antenna-ball"></div>
    <div class="antenna-stick"></div>
    <div class="ear l"></div>
    <div class="ear r"></div>
    <div class="head"></div>
    <div class="eye l"></div>
    <div class="eye r"></div>
    <div class="cheek l"></div>
    <div class="cheek r"></div>
    <div class="mouth"></div>
    <div v-if="channel" class="bot-badge" :class="channel">
      <svg v-if="channel === 'wa'" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.5 14.4c-.3-.1-1.6-.8-1.9-.9-.3-.1-.4-.1-.6.2-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.8-.7-1.4-1.6-1.6-1.9-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.6-1.5-.9-2-.2-.5-.5-.5-.7-.5h-.6c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4 0 1.4 1 2.7 1.2 2.9.2.2 2 3 4.8 4.2.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.6-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.2-.1-.4-.2-.7-.3zM21 12c0 4.97-4.03 9-9 9-1.6 0-3.09-.42-4.39-1.15L3 21l1.18-4.52C3.42 15.15 3 13.62 3 12c0-4.97 4.03-9 9-9s9 4.03 9 9z"/>
      </svg>
      <svg v-else-if="channel === 'ig'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="5"/>
        <circle cx="12" cy="12" r="4"/>
        <circle cx="17.5" cy="6.5" r=".8" fill="currentColor" stroke="none"/>
      </svg>
      <svg v-else-if="channel === 'fb'" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06C2 17.07 5.66 21.21 10.44 22v-7.03H7.9V12.06h2.54V9.84c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.24.19 2.24.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.57v1.88h2.78l-.45 2.91h-2.34V22C18.34 21.21 22 17.07 22 12.06z"/>
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.45A8.16 8.16 0 0 0 19.59 6.69z"/>
      </svg>
    </div>
  </div>
</template>

<style scoped>
.bot-avatar {
  position: relative;
  display: inline-block;
  width: var(--bot-size, 32px);
  height: var(--bot-size, 32px);
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35));
  flex-shrink: 0;
}
.head {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(180deg, #f7f7fa 0%, #e6e8ee 100%);
  box-shadow:
    inset 0 -2px 4px rgba(0, 0, 0, 0.06),
    inset 0 1px 2px rgba(255, 255, 255, 0.9);
}
.antenna-stick {
  position: absolute;
  left: 50%;
  top: calc(var(--bot-size, 32px) * -0.14);
  width: calc(var(--bot-size, 32px) * 0.04);
  height: calc(var(--bot-size, 32px) * 0.20);
  background: linear-gradient(180deg, #3b5bd6, #1e2c80);
  border-radius: 2px;
  transform: translateX(-50%);
}
.antenna-ball {
  position: absolute;
  left: 50%;
  top: calc(var(--bot-size, 32px) * -0.22);
  width: calc(var(--bot-size, 32px) * 0.18);
  height: calc(var(--bot-size, 32px) * 0.18);
  background: radial-gradient(circle at 30% 30%, #7fa1ff 0%, #3b5bd6 60%, #1e2c80 100%);
  border-radius: 50%;
  transform: translateX(-50%);
  box-shadow:
    0 0 6px rgba(59, 91, 214, 0.6),
    inset -1px -1px 1px rgba(0, 0, 0, 0.2),
    inset 1px 1px 1px rgba(255, 255, 255, 0.4);
}
.ear {
  position: absolute;
  top: 50%;
  width: calc(var(--bot-size, 32px) * 0.26);
  height: calc(var(--bot-size, 32px) * 0.26);
  background: radial-gradient(circle at 35% 35%, #a7c1ff 0%, #5d83ec 70%, #3a5bd1 100%);
  border-radius: 50%;
  transform: translateY(-50%);
  box-shadow:
    inset 0 -1px 2px rgba(0, 0, 0, 0.18),
    inset 0 1px 2px rgba(255, 255, 255, 0.35),
    0 1px 3px rgba(0, 0, 0, 0.25);
}
.ear.l { left: calc(var(--bot-size, 32px) * -0.08); }
.ear.r { right: calc(var(--bot-size, 32px) * -0.08); }
.ear::after {
  content: '';
  position: absolute;
  inset: 30%;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 40%, #2740b8 0%, #1a2570 100%);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.5);
}
.eye {
  position: absolute;
  top: 48%;
  width: calc(var(--bot-size, 32px) * 0.14);
  height: calc(var(--bot-size, 32px) * 0.06);
  border-top: calc(var(--bot-size, 32px) * 0.04) solid #2c2c33;
  border-radius: 50% 50% 0 0;
}
.eye.l { left: 24%; }
.eye.r { right: 24%; }
.mouth {
  position: absolute;
  top: 65%;
  left: 50%;
  transform: translateX(-50%);
  width: calc(var(--bot-size, 32px) * 0.16);
  height: calc(var(--bot-size, 32px) * 0.08);
  border-bottom: calc(var(--bot-size, 32px) * 0.035) solid #2c2c33;
  border-radius: 0 0 50% 50%;
}
.cheek {
  position: absolute;
  top: 60%;
  width: calc(var(--bot-size, 32px) * 0.12);
  height: calc(var(--bot-size, 32px) * 0.08);
  background: radial-gradient(circle, #ffb3c1 0%, rgba(255, 179, 193, 0) 70%);
  border-radius: 50%;
}
.cheek.l { left: 13%; }
.cheek.r { right: 13%; }
.bot-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: calc(var(--bot-size, 32px) * 0.34);
  height: calc(var(--bot-size, 32px) * 0.34);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}
.bot-badge.wa { background: linear-gradient(180deg, #25d366, #128c7e); }
.bot-badge.ig { background: linear-gradient(135deg, #f58529 0%, #dd2a7b 50%, #8134af 100%); }
.bot-badge.fb { background: #1877f2; }
.bot-badge.tt { background: #000000; }
.bot-badge svg {
  width: 62%;
  height: 62%;
  color: #fff;
}
</style>
